/**
 * Font style presets. `theme` keeps each theme's built-in typography;
 * other ids override --app-font-* on the document root.
 */
export const FONT_STYLE_IDS = ["theme", "modern", "system", "mono", "serif", "classic"] as const;
export type FontStyleId = (typeof FONT_STYLE_IDS)[number];

export const FONT_STYLE_OVERRIDE_IDS = ["modern", "system", "mono", "serif", "classic"] as const;

const STORAGE_KEY = "cpl_font_style";

export function isFontStyleId(value: unknown): value is FontStyleId {
  return typeof value === "string" && (FONT_STYLE_IDS as readonly string[]).includes(value);
}

export function migrateFontStyle(stored: string | null): FontStyleId {
  if (isFontStyleId(stored)) return stored;
  return "theme";
}

export function readStoredFontStyle(): FontStyleId {
  try {
    return migrateFontStyle(localStorage.getItem(STORAGE_KEY));
  } catch {
    return "theme";
  }
}

export function writeStoredFontStyle(id: FontStyleId): void {
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch {
    /* ignore */
  }
}

/** Preview font-family for Settings buttons */
export const FONT_STYLE_PREVIEW_FAMILY: Record<FontStyleId, string> = {
  theme: "inherit",
  modern: "'Inter', sans-serif",
  system: "system-ui, -apple-system, 'Segoe UI', sans-serif",
  mono: "'JetBrains Mono', ui-monospace, monospace",
  serif: "'Lora', Georgia, serif",
  classic: "Georgia, 'Times New Roman', serif",
};

export function applyFontStyleToDocument(id: FontStyleId): void {
  const root = document.documentElement;
  if (id === "theme") {
    root.removeAttribute("data-font-style");
  } else {
    root.setAttribute("data-font-style", id);
  }
}
